import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, alpha, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SimCardIcon from "@mui/icons-material/SimCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "../../../config/axiosConfig";

// OT "Devolución de Chips" (solo AVL/Scada): el cliente devuelve chips, tengan o no relación con un módem. Se listan los chips Activos o en Mantenimiento del cliente
// (con filtro Todos / Con módem / Sin módem); se marcan los que se devuelven (la selección se conserva al cambiar de página) y NO se modifica nada hasta finalizar la OT: ahí los chips
// pasan a Inactivo, sin cliente. "Finalizar OT" vive en la barra superior de la pantalla: esta vista avisa el total marcado con onSeleccionados
// y pide abrir la confirmación incrementando solicitudFinalizar.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11.5 } };
const OPCIONES_FILAS = [10, 25, 50, 100];

const mensajeError = (e, defecto) => e?.response?.data?.error || e?.message || defecto;

export default function DevolucionChipsOT({ cod_ot, onFinalizado, onSeleccionados, solicitudFinalizar = 0 }) {
  const [datos, setDatos] = useState({ chips: [], chips_total: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [seleccion, setSeleccion] = useState([]); // números de chip marcados
  const [observacion, setObservacion] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [relacion, setRelacion] = useState(""); // "" todos | "con" | "sin"
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const cargar = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/ot-devolucion-chips/disponibles/${cod_ot}`, {
        params: { q: q || undefined, relacion: relacion || undefined, chips_page: pagina + 1, chips_limit: limite },
      });
      setDatos(data);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar los chips del cliente."));
    } finally {
      setCargando(false);
    }
  }, [cod_ot, q, relacion, pagina, limite]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => { onSeleccionados?.(seleccion.length); }, [seleccion.length, onSeleccionados]);

  useEffect(() => { if (solicitudFinalizar > 0) setConfirmar(true); }, [solicitudFinalizar]);

  const alternar = (numero) => setSeleccion((prev) => (prev.includes(numero) ? prev.filter((x) => x !== numero) : [...prev, numero]));
  const numerosPagina = datos.chips.map((c) => c.numero);
  const todosMarcados = numerosPagina.length > 0 && numerosPagina.every((n) => seleccion.includes(n));
  const alternarPagina = () => setSeleccion((prev) => (todosMarcados
    ? prev.filter((n) => !numerosPagina.includes(n))
    : [...new Set([...prev, ...numerosPagina])]));

  const finalizar = async () => {
    setConfirmar(false);
    setTrabajando(true);
    setError("");
    try {
      const { data } = await axios.patch(`/api/ot-devolucion-chips/finalizar/${cod_ot}`, { chips: seleccion, observacion: observacion.trim() });
      onFinalizado?.(data);
    } catch (e) {
      setError(mensajeError(e, "No se pudo finalizar la OT."));
    } finally {
      setTrabajando(false);
    }
  };

  if (cargando) return <Box sx={{ p: 3, display: "grid", placeItems: "center" }}><CircularProgress size={26} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.25 }} onClose={() => setError("")}>{error}</Alert>}
      <Alert severity="info" sx={{ mb: 1.25, py: 0, fontSize: 12.5 }}>
        Marca los chips que el cliente devuelve. Aparecen los chips <b>Activos</b> o en <b>Mantenimiento</b> de este cliente, con o sin módem. Si un chip está relacionado con un módem, <b>el módem se queda con el cliente</b>, solo sin ese chip.
        Hasta que finalices la OT no se modifica nada: ahí los chips quedan <b>Inactivos</b> y libres.
      </Alert>

      <Paper variant="outlined" sx={{ p: 1.25, borderColor: (t) => alpha(t.palette.primary.main, 0.45) }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.75 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center", color: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.14) }}>
            <SimCardIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 800 }}>Chips del cliente</Typography>
          <Chip size="small" color="primary" label={datos.chips_total} sx={{ fontWeight: 800, height: 20 }} />
          {seleccion.length > 0 && <Chip size="small" color="success" label={`${seleccion.length} marcado(s) para devolver`} sx={{ fontWeight: 700, height: 20 }} />}
          {seleccion.length > 0 && <Button size="small" onClick={() => setSeleccion([])} sx={{ textTransform: "none", fontSize: 11.5 }}>Quitar marcas</Button>}
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            exclusive size="small" color="primary" value={relacion} onChange={(_, v) => { if (v !== null) { setRelacion(v); setPagina(0); } }}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 12, py: 0.25, px: 1.25 } }}
          >
            <ToggleButton value="">Todos</ToggleButton>
            <ToggleButton value="con">Con módem</ToggleButton>
            <ToggleButton value="sin">Sin módem</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small" placeholder="Buscar chip, ICC o modelo…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ width: 270 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }, htmlInput: { "aria-label": "Buscar chip" } }}
          />
        </Stack>

        <TableContainer>
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 34 }}>
                  <Checkbox size="small" checked={todosMarcados} indeterminate={!todosMarcados && numerosPagina.some((n) => seleccion.includes(n))} onChange={alternarPagina} disabled={!numerosPagina.length} sx={{ p: 0 }} inputProps={{ "aria-label": "Marcar todos de la página" }} />
                </TableCell>
                <TableCell sx={headCellSx}>Chip</TableCell>
                <TableCell sx={headCellSx}>ICC</TableCell>
                <TableCell sx={headCellSx}>Estado</TableCell>
                <TableCell sx={headCellSx}>Modelo</TableCell>
                <TableCell sx={headCellSx}>Ciudad</TableCell>
                <TableCell sx={headCellSx}>Cliente</TableCell>
                <TableCell sx={headCellSx}>Módem / relación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!datos.chips.length && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      {q ? "Ningún chip coincide con la búsqueda." : relacion === "sin" ? "Este cliente no tiene chips Activos ni en Mantenimiento sin módem." : relacion === "con" ? "Este cliente no tiene chips Activos ni en Mantenimiento con módem." : "Este cliente no tiene chips Activos ni en Mantenimiento."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {datos.chips.map((c) => (
                <TableRow key={c.numero} hover selected={seleccion.includes(c.numero)} onClick={() => alternar(c.numero)} sx={{ cursor: "pointer" }}>
                  <TableCell><Checkbox size="small" checked={seleccion.includes(c.numero)} onClick={(e) => e.stopPropagation()} onChange={() => alternar(c.numero)} sx={{ p: 0 }} /></TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{c.numero}</TableCell>
                  <TableCell>{c.emai}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.estado}
                      color={c.estado === "Mantenimiento" ? "warning" : "success"}
                      variant={c.estado === "Mantenimiento" ? "filled" : "outlined"}
                      sx={{ height: 18, fontSize: 10.5, fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>{c.modelo}</TableCell>
                  <TableCell>{c.ciudad}</TableCell>
                  <TableCell>{c.asignado}</TableCell>
                  <TableCell sx={{ color: c.relacion ? "text.primary" : "text.secondary", fontWeight: c.con_modem ? 700 : 400 }}>{c.relacion ? (c.con_modem ? `Módem ${c.relacion}` : c.relacion) : "Sin relación"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {datos.chips_total > 0 && (
          <TablePagination
            component="div" count={datos.chips_total} page={pagina} rowsPerPage={limite} rowsPerPageOptions={OPCIONES_FILAS}
            onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
            labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ "& .MuiTablePagination-toolbar": { minHeight: 36 }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12, m: 0 } }}
          />
        )}
      </Paper>

      <Dialog open={confirmar} onClose={() => setConfirmar(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
          <CheckCircleIcon color="success" sx={{ verticalAlign: "middle", mr: 0.75 }} />
          Finalizar OT
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1.25 }}>
            Se devolverán <b>{seleccion.length} chip(s)</b>: quedarán <b>Inactivos</b> y sin cliente; los que tenían módem dejan de estar relacionados con él (el módem sigue con el cliente). Después ya no se puede deshacer desde la OT.
          </Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, maxHeight: 120, overflowY: "auto" }}>
            {seleccion.map((n) => <Chip key={n} size="small" color="primary" variant="outlined" icon={<SimCardIcon />} label={n} />)}
          </Stack>
          <TextField size="small" fullWidth multiline minRows={2} label="Observación (opcional)" value={observacion} onChange={(e) => setObservacion(e.target.value)} slotProps={{ htmlInput: { maxLength: 250 } }} sx={{ mt: 1.5 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)}>Cancelar</Button>
          <Button variant="contained" autoFocus disabled={trabajando || !seleccion.length} onClick={finalizar}>Confirmar devolución</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
